import { Skeleton } from "../ui/skeleton";

type Variant = "list" | "table" | "cards" | "dashboard" | "profile" | "detail";
export type { Variant };

interface Props {
  variant?: Variant;
  className?: string;
}

/* Shared building blocks ------------------------------------------------ */

function HeaderBar() {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Skeleton className="h-10 w-72 max-w-full rounded-lg" />
      <Skeleton className="h-4 w-[28rem] max-w-full rounded-md" />
    </div>
  );
}

function FilterBar() {
  return (
    <div className="border border-border bg-card rounded-2xl p-4 md:p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
        <div className="sm:col-span-2 space-y-2">
          <Skeleton className="h-3 w-16 rounded" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-16 rounded" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function DepartmentCard() {
  return (
    <div className="h-full rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-5 w-16 rounded font-mono" />
          <Skeleton className="h-4 w-40 rounded" />
        </div>
        <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
      </div>
      <Skeleton className="h-5 w-20 rounded-full" />
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="p-3 rounded-xl bg-background border border-border text-center space-y-2"
          >
            <Skeleton className="w-3.5 h-3.5 mx-auto rounded" />
            <Skeleton className="h-3 w-6 mx-auto rounded" />
            <Skeleton className="h-2 w-10 mx-auto rounded" />
          </div>
        ))}
      </div>
      <Skeleton className="h-9 w-full rounded-xl" />
    </div>
  );
}

function CourseCard() {
  return (
    <div className="h-full rounded-2xl border border-border bg-card overflow-hidden">
      <Skeleton className="h-36 w-full rounded-none" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-2/3 rounded" />
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className="h-3 w-4/5 rounded" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function StatCard() {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
      <Skeleton className="h-4 w-20 rounded" />
      <Skeleton className="h-8 w-16 rounded" />
      <Skeleton className="h-2 w-full rounded-full" />
    </div>
  );
}

/* Variant: dashboard (departments / courses / learn / admin / analytics) */

function DashboardSkeleton() {
  return (
    <div className="space-y-10">
      <HeaderBar />
      <FilterBar />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[0, 1, 2, 3].map((i) => (
          <DepartmentCard key={i} />
        ))}
      </div>
    </div>
  );
}

/* Variant: cards (flashcards / badges / points-shop / saved) */

function CardsSkeleton() {
  return (
    <div className="space-y-8">
      <HeaderBar />
      <FilterBar />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <CourseCard key={i} />
        ))}
      </div>
    </div>
  );
}

/* Variant: table (leaderboard / admin tables / invoices) */

function TableSkeleton() {
  return (
    <div className="space-y-6">
      <HeaderBar />
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 flex-1 rounded-xl" />
        <Skeleton className="h-10 w-28 rounded-xl" />
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-4 border-b border-border p-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-4 flex-1 rounded" />
          ))}
        </div>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border p-4 last:border-b-0"
          >
            <Skeleton className="w-9 h-9 rounded-full shrink-0" />
            <Skeleton className="h-4 flex-[2] rounded" />
            <Skeleton className="h-4 flex-1 rounded hidden sm:block" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Variant: list (events / challenge / quests / notifications / syllabus) */

function ListSkeleton() {
  return (
    <div className="space-y-8">
      <HeaderBar />
      <FilterBar />
      <div className="space-y-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5"
          >
            <Skeleton className="w-12 h-12 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2 rounded" />
              <Skeleton className="h-3 w-3/4 rounded" />
              <Skeleton className="h-3 w-2/5 rounded" />
            </div>
            <Skeleton className="h-6 w-16 rounded-full shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Variant: profile (profile / user pages) */

function ProfileSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-5">
        <Skeleton className="w-20 h-20 rounded-full shrink-0" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-6 w-48 rounded" />
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <StatCard key={i} />
        ))}
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <Skeleton className="h-5 w-40 rounded" />
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className="h-3 w-5/6 rounded" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    </div>
  );
}

/* Variant: detail (events/[id] / single-item pages) */

function DetailSkeleton() {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="space-y-4">
        <Skeleton className="h-5 w-24 rounded-full" />
        <Skeleton className="h-9 w-2/3 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left: details card */}
        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start gap-4">
                <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-20 rounded" />
                  <Skeleton className="h-4 w-1/2 rounded" />
                  {i === 4 && (
                    <>
                      <Skeleton className="h-3 w-full rounded" />
                      <Skeleton className="h-3 w-4/5 rounded" />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: actions + related materials */}
        <div className="lg:col-span-5 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <Skeleton className="h-9 w-full rounded-xl" />
            <div className="p-4 rounded-xl bg-background border border-border space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-16 rounded" />
                <Skeleton className="h-3 w-12 rounded" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-8 flex-1 rounded-xl" />
                <Skeleton className="h-8 flex-1 rounded-xl" />
              </div>
            </div>
            <Skeleton className="h-9 w-full rounded-xl" />
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
            <Skeleton className="h-4 w-32 rounded" />
            {[0, 1].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border"
              >
                <Skeleton className="w-4 h-4 rounded shrink-0" />
                <Skeleton className="h-3 flex-1 rounded" />
                <Skeleton className="w-3 h-3 rounded shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="rounded-2xl border border-border bg-card">
        <div className="p-6 space-y-4">
          <Skeleton className="h-4 w-28 rounded" />
          <div className="flex gap-3">
            <Skeleton className="flex-1 h-16 rounded-xl" />
            <Skeleton className="w-16 h-16 rounded-xl shrink-0" />
          </div>
          {[0, 1].map((i) => (
            <div
              key={i}
              className="p-4 rounded-xl bg-background border border-border space-y-2"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-24 rounded" />
                <Skeleton className="h-3 w-20 rounded" />
              </div>
              <Skeleton className="h-3 w-full rounded" />
              <Skeleton className="h-3 w-4/5 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Exported component ---------------------------------------------------- */

export const FullPageSkeleton = ({
  variant = "list",
  className = "",
}: Props) => {
  const render = () => {
    switch (variant) {
      case "dashboard":
        return <DashboardSkeleton />;
      case "cards":
        return <CardsSkeleton />;
      case "table":
        return <TableSkeleton />;
      case "profile":
        return <ProfileSkeleton />;
      case "detail":
        return <DetailSkeleton />;
      case "list":
      default:
        return <ListSkeleton />;
    }
  };

  return (
    <div data-variant={variant} className={`p-4 md:p-8 ${className}`}>
      {render()}
    </div>
  );
};
